"use client";
import React from "react";
import { motion, type Variants } from "motion/react";
import { useMediaQuery } from "@/hooks/use-media-query";

interface FadeInProps {
  children: React.ReactNode;
  className?: string;
  delay?: number;
  duration?: number;
  direction?: "up" | "down" | "left" | "right" | "none";
  mobileDirection?: "up" | "down" | "left" | "right" | "none";
  distance?: number;
  once?: boolean;
  scale?: number;
  as?: React.ElementType;
}

const FadeIn: React.FC<FadeInProps> = ({
  children,
  className,
  delay = 0,
  duration = 0.6,
  direction = "up",
  mobileDirection,
  distance = 30,
  once = true,
  scale,
  as = "div",
}) => {
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const activeDirection = mobileDirection && !isDesktop ? mobileDirection : direction;

  const directionMap: Record<string, { x?: number; y?: number }> = {
    up: { y: distance },
    down: { y: -distance },
    left: { x: distance },
    right: { x: -distance },
    none: {},
  };

  const offset = directionMap[activeDirection] ?? {};

  const variants: Variants = {
    hidden: {
      opacity: 0,
      ...offset,
      ...(scale !== undefined ? { scale } : {}),
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      ...(scale !== undefined ? { scale: 1 } : {}),
    },
  };

  const MotionComp = motion.create(as);

  return (
    <MotionComp
      className={className}
      variants={variants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once, margin: "-50px" }}
      transition={{
        duration,
        delay,
        ease: [0.25, 0.1, 0.25, 1],
      }}
    >
      {children}
    </MotionComp>
  );
};

export default FadeIn;
